import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Facebook, Instagram, Linkedin, Twitter } from "lucide-react";
import { useContent } from "@/content/ContentContext";
import { useLang } from "@/i18n/LanguageContext";

const ease = [0.22, 1, 0.36, 1];

export default function Footer() {
  const content = useContent();
  const { t } = useLang();
  const c = content.contact;
  return (
    <footer
      data-testid="site-footer"
      className="relative overflow-hidden border-t border-line"
    >
      <div className="mx-auto max-w-[1440px] px-6 pt-24 lg:px-12 lg:pt-32">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <motion.h2
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, ease }}
              data-testid="footer-cta-heading"
              className="font-display text-5xl font-medium leading-[1.02] tracking-tighter text-cream sm:text-6xl lg:text-7xl"
            >
              {t("footerCtaA")} <span className="italic text-harvest">{t("footerCtaAccent")}</span>
            </motion.h2>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: 0.1, ease }}
            >
              <Link
                to="/contact"
                data-testid="footer-enquire-button"
                className="group mt-10 inline-flex items-center gap-3 bg-harvest px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-forest transition-colors duration-300 hover:bg-harvestHover"
              >
                {t("startEnquiry")}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </motion.div>
            <p className="mt-10 max-w-sm text-sm leading-relaxed text-sage">
              {t("brandBlurb")}
            </p>
          </div>
          <div className="lg:col-span-2">
            <p className="text-xs uppercase tracking-[0.3em] text-harvest">
              {t("colProducts")}
            </p>
            <div className="mt-6 space-y-3 text-sm text-sage">
              {content.products.map((p) => (
                <Link key={p.id} to={`/products/${p.id}`} data-testid={`footer-link-${p.id}`} className="block transition-colors duration-300 hover:text-harvest">
                  {p.name}
                </Link>
              ))}
            </div>
          </div>
          <div className="lg:col-span-2">
            <p className="text-xs uppercase tracking-[0.3em] text-harvest">
              {t("colCompany")}
            </p>
            <div className="mt-6 space-y-3 text-sm text-sage">
              <Link to="/about" data-testid="footer-link-legacy" className="block transition-colors duration-300 hover:text-harvest">{t("footerLegacy")}</Link>
              <Link to="/about#team" data-testid="footer-link-team" className="block transition-colors duration-300 hover:text-harvest">{t("footerTeam")}</Link>
              <Link to="/stories" data-testid="footer-link-stories" className="block transition-colors duration-300 hover:text-harvest">{t("footerStories")}</Link>
              <Link to="/crops" data-testid="footer-link-crops" className="block transition-colors duration-300 hover:text-harvest">{t("footerCrops")}</Link>
              <Link to="/updates" data-testid="footer-link-updates" className="block transition-colors duration-300 hover:text-harvest">{t("footerUpdates")}</Link>
              <Link to="/contact?interest=dealership" data-testid="footer-link-dealer" className="block transition-colors duration-300 hover:text-harvest">{t("footerDealer")}</Link>
            </div>
          </div>
          <div className="lg:col-span-3">
            <p className="text-xs uppercase tracking-[0.3em] text-harvest">
              {t("colContact")}
            </p>
            <div className="mt-6 space-y-2 text-sm text-sage">
              <a
                href={`tel:${c.phone.replace(/\s/g, "")}`}
                data-testid="footer-phone-link"
                className="block transition-colors duration-300 hover:text-harvest"
              >
                {c.phone}
              </a>
              <a
                href={`mailto:${c.email}`}
                data-testid="footer-email-link"
                className="block transition-colors duration-300 hover:text-harvest"
              >
                {c.email}
              </a>
              <p data-testid="footer-address" className="text-sage/70">
                {c.address}
              </p>
              {c.gstin && (
                <p data-testid="footer-gstin" className="text-sage/70">
                  GSTIN: {c.gstin}
                </p>
              )}
              <div className="flex items-center gap-3 pt-4" data-testid="footer-social">
                {[
                  { key: "facebook", Icon: Facebook, testid: "social-facebook" },
                  { key: "instagram", Icon: Instagram, testid: "social-instagram" },
                  { key: "twitter", Icon: Twitter, testid: "social-twitter" },
                  { key: "linkedin", Icon: Linkedin, testid: "social-linkedin" },
                ].map(({ key, Icon, testid }) => {
                  const url = (content.contact.social || {})[key];
                  if (!url) return null;
                  return (
                    <a
                      key={key}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={key}
                      data-testid={testid}
                      className="border border-line p-2.5 text-sage transition-colors duration-300 hover:border-harvest hover:text-harvest"
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-20 flex flex-col gap-3 border-t border-line py-8 text-xs uppercase tracking-[0.2em] text-sage/70 sm:flex-row sm:items-center sm:justify-between">
          <p data-testid="footer-copyright">&#169; 2026 Jamna Industries. All rights reserved.</p>
          <p>{t("madeIn")}</p>
        </div>
      </div>

      <p
        aria-hidden="true"
        className="pointer-events-none select-none whitespace-nowrap text-center font-display text-[13vw] font-bold leading-none tracking-tight text-cream/5"
      >
        JAMNA INDUSTRIES
      </p>
    </footer>
  );
}
